/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  useForm,
  useFetch,
  useApiClient,
  getFieldProps,
  getFormProps,
  getSubmitButtonProps,
  OutlinedIcons,
} from '@marageti/z4-lib';
import { PasswordConfig } from '@marageti/z4-sdk/lib/auth';
import {
  IconButton, InputAdornment, Stack, TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { PasswordFormProps } from '../../pages/reset/set-password/types';

const { OutlinedEyeIcon, OutlinedEyeOffIcon } = OutlinedIcons;

const useValidationSchema = () => {
  const apiClient = useApiClient();
  const { data: passwordConfig } = useFetch<PasswordConfig>(apiClient.authClient.getPasswordConfig, [], true);

  const schema = z.object({
    password: z.string().min(1, 'Please enter a new password')
      .refine(async (value) => {
        if (!passwordConfig) return false;
        const regex = new RegExp(passwordConfig.filter);
        return regex.test(value);
      }, {
        message: passwordConfig?.description,
      }),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

  return schema;
};

const PasswordForm = ({
  email,
  onSubmit,
}: PasswordFormProps) => {
  const schema = useValidationSchema();
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(schema),
  });

  return (
    <form
      name="reset-form"
      {...getFormProps(handleSubmit(onSubmit))}
    >
      <Stack spacing={3} width={340}>
        <Stack>
          {/* hidden username/email field for accessibility */}
          <TextField
            autoComplete="username"
            disabled
            hidden
            name="email"
            style={{ display: 'none' }}
            value={email}
          />
          <TextField
            autoComplete="new-password"
            id="password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <OutlinedEyeOffIcon color="primary" /> : <OutlinedEyeIcon color="primary" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            {...register('password')}
            {...getFieldProps('password', errors)}
          />

          <TextField
            autoComplete="confirm-password"
            id="confirmPassword"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <OutlinedEyeOffIcon color="primary" /> : <OutlinedEyeIcon color="primary" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            {...register('confirmPassword')}
            {...getFieldProps('confirmPassword', errors)}
          />
        </Stack>

        <LoadingButton
          type="submit"
          variant="contained"
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          Set Password
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default PasswordForm;
